import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Tv, ListVideo, Palette, Package, CreditCard,
  Settings, Users, BarChart3, ShieldCheck, LogOut, Calendar, Building2,
  ChevronLeft, ChevronRight, MessageCircle, Phone, LifeBuoy, Handshake,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils/cn';
import { MyShowLogoFull, MyShowMark } from '@/components/Logo';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/stores', label: 'Lojas', icon: Building2 },
  { to: '/tvs', label: 'TVs', icon: Tv },
  { to: '/tvs/sessions', label: 'Calendário de uso', icon: Calendar },
  { to: '/playlists', label: 'Playlists', icon: ListVideo },
  { to: '/themes', label: 'Temas', icon: Palette },
  { to: '/products', label: 'Produtos', icon: Package },
  { to: '/billing', label: 'Cobrança', icon: CreditCard },
  { to: '/settings', label: 'Configurações', icon: Settings },
  { to: '/ajuda', label: 'Ajuda', icon: LifeBuoy },
];

const adminItems = [
  { to: '/admin', label: 'Visão geral', icon: BarChart3 },
  { to: '/admin/tenants', label: 'Tenants', icon: Users },
  { to: '/admin/partners', label: 'Parceiros', icon: Handshake },
  { to: '/admin/billing', label: 'Faturamento', icon: CreditCard },
];

// Contatos de suporte da Visual Software — atalhos no rodapé pro cliente
// falar com a gente direto pelo painel.
const supportContacts = [
  { href: 'https://wa.me/5546991136456', label: 'Suporte técnico', icon: MessageCircle, iconBg: 'bg-emerald-100 text-emerald-600', hover: 'hover:bg-emerald-50', external: true },
  { href: 'https://wa.me/5546998262101', label: 'Comercial',       icon: MessageCircle, iconBg: 'bg-emerald-100 text-emerald-600', hover: 'hover:bg-emerald-50', external: true },
  { href: 'tel:+554635322005',          label: '(46) 3532-2005',   icon: Phone,         iconBg: 'bg-brand-100 text-brand-600',     hover: 'hover:bg-brand-50',   external: false },
];

export function Sidebar() {
  const { signOut, isMasterAdmin, user } = useAuthStore();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { /* noop */ }
      return next;
    });
  };

  return (
    <aside className={cn(
      'hidden lg:flex flex-col bg-card border-r border-border transition-all duration-200 shrink-0',
      collapsed ? 'w-16' : 'w-64'
    )}>
      {/* Header */}
      <div className={cn(
        'flex h-16 items-center border-b border-border bg-card shrink-0',
        collapsed ? 'justify-center' : 'justify-between px-4'
      )}>
        {collapsed ? (
          <button
            onClick={toggle}
            title="Expandir menu"
            className="flex flex-col items-center gap-1 p-1 rounded hover:bg-muted transition-colors overflow-hidden"
          >
            <div className="h-5 w-5 overflow-hidden flex items-start justify-center">
              <MyShowMark size={20} />
            </div>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          </button>
        ) : (
          <>
            <MyShowLogoFull height={36} />
            <button
              onClick={toggle}
              title="Recolher menu"
              className="p-1.5 rounded hover:bg-muted text-muted-foreground transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {!collapsed && (
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2 px-3">
            Principal
          </div>
        )}
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) => cn(
              'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
              collapsed ? 'justify-center' : 'gap-3',
              isActive ? 'bg-brand-100 text-brand-800' : 'text-foreground hover:bg-muted'
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && item.label}
          </NavLink>
        ))}

        {isMasterAdmin && (
          <>
            {collapsed
              ? <div className="border-t border-border my-2 mx-1" />
              : (
                <div className="text-xs font-semibold text-muted-foreground uppercase mt-6 mb-2 px-3 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Master Admin
                </div>
              )
            }
            {adminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) => cn(
                  'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  collapsed ? 'justify-center' : 'gap-3',
                  isActive ? 'bg-amber-100 text-amber-900' : 'text-foreground hover:bg-muted'
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {!collapsed && item.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-2 shrink-0">
        {/* Fale conosco — contatos de suporte */}
        {collapsed ? (
          <div className="flex flex-col items-center gap-1.5 mb-2 pb-2 border-b border-border">
            {supportContacts.map((c) => (
              <a
                key={c.href}
                href={c.href}
                title={c.label}
                target={c.external ? '_blank' : undefined}
                rel={c.external ? 'noreferrer' : undefined}
                className="grid place-items-center h-9 w-9 rounded-full transition-transform hover:scale-110"
              >
                <span className={cn('grid place-items-center h-8 w-8 rounded-full', c.iconBg)}>
                  <c.icon className="h-4 w-4" />
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="mb-3 pb-3 border-b border-border">
            <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-1.5 px-2">
              Fale conosco
            </div>
            <div className="space-y-0.5">
              {supportContacts.map((c) => (
                <a
                  key={c.href}
                  href={c.href}
                  target={c.external ? '_blank' : undefined}
                  rel={c.external ? 'noreferrer' : undefined}
                  className={cn(
                    'group flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-sm font-medium text-foreground transition-colors',
                    c.hover
                  )}
                >
                  <span className={cn('grid place-items-center h-7 w-7 rounded-full shrink-0 transition-transform group-hover:scale-105', c.iconBg)}>
                    <c.icon className="h-4 w-4" />
                  </span>
                  <span className="truncate">{c.label}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {collapsed ? (
          <>
            <button
              onClick={toggle}
              title={user?.email || undefined}
              className="flex justify-center w-full rounded-md p-2 mb-1 hover:bg-muted transition-colors"
            >
              <div className="h-7 w-7 rounded-full bg-brand-100 grid place-items-center text-brand-700 text-xs font-bold">
                {user?.email?.[0]?.toUpperCase() ?? '?'}
              </div>
            </button>
            <button
              onClick={signOut}
              title="Sair"
              className="flex justify-center w-full rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 px-2">
              <div className="h-8 w-8 rounded-full bg-brand-100 grid place-items-center text-brand-700 text-xs font-bold shrink-0">
                {user?.email?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" title={user?.email || undefined}>{user?.email}</div>
              </div>
              <button
                onClick={signOut}
                title="Sair"
                className="flex items-center gap-1 shrink-0 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
